/**
 * The formed wordmark's choreography.
 *
 * A wavefront crosses the swarm and every drone it touches opens its safe
 * zone (0.1 lu -> 1.0 lu) for two seconds, then falls back. The neighbours
 * shove apart as it passes, so the letters ripple.
 *
 * Deliberately STATELESS: a drone's radius is a pure function of program
 * time and its own slot position. Nothing accumulates, nothing drifts, and
 * there is no timer to block, cancel, or leak. Pure module, so the whole
 * program is testable by import with no renderer at all.
 */

export type SweepKind = "all" | "lr" | "tb" | "rl" | "bt" | "out" | "in";

/** Free's order: everyone at once, the four compass sweeps, then out and in. */
export const PROGRAM: SweepKind[] = ["all", "lr", "tb", "rl", "bt", "out", "in"];

export const HOLD = 2; // seconds a crossed drone stays open
export const WAIT = 5; // dead time after a pattern finishes
/**
 * Seconds for a wavefront to cross the wordmark. The lit band is HOLD/TRAVEL
 * of the whole width, so a LONGER travel reads as a narrower moving line:
 * at 4.5 s the band covers a bit under half the wordmark, which reads as a
 * sweep rather than the whole sign opening at once.
 */
export const TRAVEL = 4.5;

export type SweepCoords = {
  nx: Float32Array; // 0 at the left edge, 1 at the right
  ny: Float32Array; // 0 at the bottom, 1 at the top
  nr: Float32Array; // 0 at the centre, 1 at the farthest drone
};

/** Normalized slot coordinates, computed once from the wordmark positions. */
export function sweepCoords(slots: Float32Array): SweepCoords {
  const n = slots.length / 3;
  const nx = new Float32Array(n);
  const ny = new Float32Array(n);
  const nr = new Float32Array(n);
  let minx = Infinity, maxx = -Infinity, miny = Infinity, maxy = -Infinity;
  for (let i = 0; i < n; i++) {
    const x = slots[i * 3], y = slots[i * 3 + 1];
    if (x < minx) minx = x;
    if (x > maxx) maxx = x;
    if (y < miny) miny = y;
    if (y > maxy) maxy = y;
  }
  const w = maxx - minx || 1;
  const h = maxy - miny || 1;
  const cx = (minx + maxx) / 2, cy = (miny + maxy) / 2;
  let maxr = 1e-6;
  for (let i = 0; i < n; i++) {
    nx[i] = (slots[i * 3] - minx) / w;
    ny[i] = (slots[i * 3 + 1] - miny) / h;
    const r = Math.hypot(slots[i * 3] - cx, slots[i * 3 + 1] - cy);
    nr[i] = r;
    if (r > maxr) maxr = r;
  }
  for (let i = 0; i < n; i++) nr[i] /= maxr;
  return { nx, ny, nr };
}

const travelOf = (k: SweepKind) => (k === "all" ? 0 : TRAVEL);
const moveDur = (k: SweepKind) => travelOf(k) + HOLD + WAIT;

/** Length of one full pass through the program, in seconds. */
export const CYCLE = PROGRAM.reduce((a, k) => a + moveDur(k), 0);

/** Which move is running at program time t, and how far into it we are. */
export function moveAt(t: number): { kind: SweepKind; lt: number; index: number } {
  let tc = t % CYCLE;
  if (tc < 0) tc += CYCLE;
  for (let i = 0; i < PROGRAM.length; i++) {
    const d = moveDur(PROGRAM[i]);
    if (tc < d) return { kind: PROGRAM[i], lt: tc, index: i };
    tc -= d;
  }
  return { kind: PROGRAM[0], lt: 0, index: 0 };
}

/** Where drone i sits along this move's wavefront, as a fraction 0..1. */
export function crossFrac(kind: SweepKind, c: SweepCoords, i: number): number {
  switch (kind) {
    case "all": return 0;
    case "lr": return c.nx[i];
    case "rl": return 1 - c.nx[i];
    case "bt": return c.ny[i];
    case "tb": return 1 - c.ny[i];
    case "out": return c.nr[i];
    case "in": return 1 - c.nr[i];
  }
}

/**
 * Write every drone's safe zone (in LIGHT UNITS) for program time t.
 * Returns the move that is running, which is all the state there is.
 */
export function fillSafeLu(
  t: number,
  c: SweepCoords,
  out: Float32Array,
  restLu: number,
  openLu: number
): SweepKind {
  const { kind, lt } = moveAt(t);
  const travel = travelOf(kind);
  for (let i = 0; i < out.length; i++) {
    const start = crossFrac(kind, c, i) * travel;
    out[i] = lt >= start && lt < start + HOLD ? openLu : restLu;
  }
  return kind;
}
